"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cn } from "@/lib/utils";
import { CheckLineIcon } from "@/assets/icons/check-line";
import { MinusIcon } from "@/assets/icons/minus";

const Checkbox = React.memo(
  React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> & {
      indeterminate?: boolean;
    }
  >(({ className, indeterminate, ...props }, ref) => {
    const isActive = props.checked || indeterminate;
    const isDisabled = props.disabled;

    return (
      <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
          "peer ring-offset-background focus-visible:ring-ring h-5 w-5 shrink-0 cursor-pointer rounded-md border border-neutral-800 duration-200 ease-in-out hover:border-neutral-600 hover:shadow-sm focus-visible:ring-1 focus-visible:ring-offset-2 focus-visible:outline-none active:scale-95 disabled:cursor-not-allowed",
          isActive &&
            "bg-primary hover:bg-primary/90 cursor-pointer border-none text-white",
          isDisabled &&
            "border-black-300 bg-current/10 text-neutral-500 opacity-100",
          className,
        )}
        {...props}
      >
        <div className="relative flex h-full w-full items-center justify-center overflow-hidden">
          {/* Check icon */}
          <div
            className={cn(
              "absolute flex items-center justify-center text-current",
              props.checked && !indeterminate
                ? "scale-100 rotate-0 opacity-100"
                : "scale-75 -rotate-90 opacity-0",
            )}
          >
            <CheckLineIcon className="h-4 w-4" />
          </div>

          {/* Indeterminate icon */}
          <div
            className={cn(
              "absolute flex items-center justify-center text-current",
              indeterminate
                ? "scale-100 rotate-0 opacity-100"
                : "scale-75 rotate-90 opacity-0",
              isDisabled && "text-white",
            )}
          >
            <MinusIcon className="h-4 w-4" />
          </div>
        </div>
      </CheckboxPrimitive.Root>
    );
  }),
);
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

export { Checkbox };
